import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip, Icon, Popconfirm } from 'antd';
import { BlockHighlighter, DynamicCellSettings, PageActionItem } from './styles';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import { FOOTER, HEADER, TEXT_BLOCK } from '../../utils/complexDocumentConstants';
import { useTranslation } from 'react-i18next';
import { BLOCK_SETTINGS_OPTIONS } from './translationValues';
import ComplexBlockSettings from '../../components/ComplexBlockSettings';
import { CONTEXT_KEYS, useArtboardContext, TEMPLATE_TYPE } from '../../context/complexArtboardContext';

const NAMESPACES = ['BLOCK_SETTINGS_OPTIONS'];
const BlockLayoutSettings = ({ pageId, parentUUID, customRef, isEmailTemplate = false }) => {
  const [blockElement, setBlockElement] = useState(undefined);

  const itemUUID = blockElement?.blockId;

  const customSelector = useCallback(store => store[itemUUID], [itemUUID]);
  const [item, methods] = useDocumentContext(customSelector, `Block-${itemUUID}`);

  const [picker, setPicker] = useState(false);
  const [toggleMovement, setToggleMovement] = useState(false);
  const [isBlockClick, toggleBlockClick] = useState(false);
  const [blockStyles, setBlockStyles] = useState({});
  const [selectedBlockStyle, setSelectedBlockStyle] = useState({});
  const [pageScrolling, setPageScrolling] = useState(false);
  const { t } = useTranslation(NAMESPACES);

  const sidebarSelector = useCallback(store => store[CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE], []);
  const [blockSettingsVisible, updateArtboard] = useArtboardContext(sidebarSelector, `BlockLayoutSettings-${parentUUID}`);

  const blockSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK_SETTINGS], []);
  const [selectedBlockItem] = useArtboardContext(blockSelector, `BlockLayoutSettings-${parentUUID}`);
  
  const blockHighlightSelector = useCallback((store) => store[CONTEXT_KEYS.BLOCK_NAVIGATE_AND_HIGHLIGHT], []);
  const [isHighlight] = useArtboardContext(blockHighlightSelector);

  const blockIdSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_BLOCK_ID], []);
  const [currentBlockID] = useArtboardContext(blockIdSelector);

  const flowIdSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_FLOW_ID], []);
  const [currentFlowId] = useArtboardContext(flowIdSelector);

  const currentDesignSelector = useCallback((store) => [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]), []);
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, '');

  const setSelectedBlock = useCallback((selectedBlock) => {    
    updateArtboard({ [CONTEXT_KEYS.CURRENT_BLOCK_SETTINGS]: selectedBlock });
  }, []);

  const showSideBar = blockSettingsVisible && selectedBlockItem?.pageId === pageId;

  const editingSelector = useCallback(
    store => store[CONTEXT_KEYS.CURRENT_PAGE] && store[CONTEXT_KEYS.CURRENT_BLOCK] !== '',
    [],
  );
  const [blockEditing] = useArtboardContext(editingSelector, 'BlockLayoutSettings');

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector);

  const selectedBlockSelector = useCallback(store => store[CONTEXT_KEYS.SELECTED_BLOCKS], []);
  const [selectedBlocks] = useArtboardContext(selectedBlockSelector, `TextBlock-${itemUUID}`);

  const standaloneBlockEditSelector = useCallback(store => store[CONTEXT_KEYS.STANDALONE_BLOCK_EDIT], []);
  const [standaloneBlockEditUUID] = useArtboardContext(standaloneBlockEditSelector, `TextBlock-${itemUUID}`);

  const  blockTypeSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_BLOCK_TYPE], []);
  const [reusableBlockType] = useDocumentContext(blockTypeSelector,`TextBlock-${itemUUID}`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `BlockLayoutSettings-${itemUUID}`);
  
  const blockSelected = selectedBlocks.includes(itemUUID);

  const getElementStyles = useCallback((e, blockId, isHeaderOrFooter = false) => {
    if (e) {
      setBlockElement({
        target: e,
        clientY: e.clientY,
        blockId: blockId,
        pageId: pageId,
        isHeaderOrFooter: isHeaderOrFooter, // Need for this key is no longer valid, todo remove the key
      });
    } else {
      setBlockElement(undefined);
    }
  }, [pageId]);

  useEffect(() => {
    let target = document.getElementById(currentBlockID);
    if (item?.blockType === TEXT_BLOCK) {
      target = document.getElementById(`q-container-${currentBlockID}`);
    }
    if (target && currentFlowId) {
      let styles = target?.getBoundingClientRect();
      const editingContainer = document.getElementById(currentFlowId);
      const pageContent = editingContainer.getBoundingClientRect();
      let blockAbsoluteStyles = {
        top: parseFloat(styles?.top - pageContent.top).toFixed(3) + 'px',
        left: parseFloat(styles?.left - pageContent.left).toFixed(3) + 'px',
        width: parseFloat(styles?.width - 2).toFixed(3) + 'px',
        height: parseFloat(styles?.height).toFixed(3) + 'px',
        isVisible: 'visible',
      };
      setSelectedBlockStyle(blockAbsoluteStyles);
    }
  }, [isHighlight]);

  useEffect(() => {
    if (customRef) {
      customRef(existingValue => {
        return getElementStyles;
      });
    }
  }, [getElementStyles]);

  useEffect(() => {
    if (!blockSettingsVisible) {
      setSelectedBlock(null);
      setSelectedBlockStyle({});
    }
  }, [blockSettingsVisible]);

  useEffect(() => {
    if (selectedBlockItem?.itemUUID) {
      if (selectedBlockItem?.item?.blockType === TEXT_BLOCK) {
        let targetStyle = document
          ?.getElementById(`q-container-${selectedBlockItem?.itemUUID}`)
          ?.getBoundingClientRect();
        if (selectedBlockStyle.height) {
          setSelectedBlockStyle({ ...selectedBlockStyle, height: `${targetStyle?.height}px` });
        }
      }
    }
    setPicker(false);
  }, [item]);

  useEffect(() => {
    blockElement?.target && getBlockStyles(blockElement);
  }, [blockElement]);

  const togglePointer = useCallback(e => {
    toggleBlockClick(e);
  }, []);

  const getBlockStyles = useCallback(
    blockElement => {
      let { clientY, blockId, isHeaderOrFooter } = blockElement;
      let positionTop, positionRight;
      let target = document.getElementById(`editingContainer-${pageId}`);
      if (target) {
        var rect = target?.getBoundingClientRect();
        let pageTarget = document.getElementById(`complex-page-${pageId}`);
        var pageRect = pageTarget?.getBoundingClientRect();
        positionTop =
          rect.top > 0
            ? Math.abs(clientY - Math.abs(rect.top) - 30)
            : Math.abs(clientY) + Math.abs(rect.top) - 30;
          positionRight = rect.right - pageRect.right - 40;
      }
      let styles = {
        top: `${positionTop}px`,
        right: `${positionRight}px`,
        blockId: blockId,
        pageRect: rect,
      };
      setBlockStyles(styles);
    },
    [blockElement],
  );

  const onDeleteBlockClick = useCallback(() => {
    let deleteItem = item;
    if (deleteItem) {
      let isStaticElement = deleteItem.type === HEADER || deleteItem.type === FOOTER;
      if (isStaticElement) {
        methods.setPage(
          'deleteStaticElement',
          {
            ...deleteItem,
            uuid: parentUUID,
          },
          true,
        );
      } else if (deleteItem.isChild) {
        methods.updateBlock('deleteChildBlock', deleteItem.parentBlockUUID, {
          block: deleteItem,
          pageId,
          flowUUID: parentUUID
        })
      } else {
        methods.setPage(
          'deleteBlock',
          {
            parentUUID,
            block: deleteItem,
            pageId,
            flowUUID: parentUUID,
            uuid: parentUUID,
          },
          true,
        );
      }
    }
    closeSideBarSettings();
    setBlockElement({});
    setToggleMovement(false);
    
    setTimeout(() => {
      onPageScrollEnd();
    },1100); 
    
  }, [item]);

  let absoluteStyles = blockStyles.blockId
    ? document.getElementById(blockStyles.blockId)?.getBoundingClientRect()
    : undefined;
  if (absoluteStyles && blockStyles.pageRect) {
    absoluteStyles = {
      top: parseFloat(absoluteStyles?.top - blockStyles.pageRect?.top).toFixed(3) + 'px',
      left: parseFloat(absoluteStyles?.left - blockStyles.pageRect?.left).toFixed(3) + 'px',
      width: parseFloat(absoluteStyles?.width).toFixed(3) + 'px',
      height: parseFloat(absoluteStyles?.height).toFixed(3) + 'px',
    };
  }

  const openSettingsInSidebar = useCallback(
    (open = true) => {
      if (open) {
        updateArtboard({
          [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
          [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: true,
        });
      } else {
        updateArtboard({
          [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false,
        });
      }
    },
    [itemUUID],
  );

  const changeSettingOfItem = useCallback(
    item => {
      removeHighlightOfItem();
      let selectItem = {
        item: item,
        itemUUID: itemUUID,
        parentUUID: parentUUID,
        pageId: pageId,
      };
      addHighlightToItem(selectItem, blockStyles);
      setSelectedBlock(selectItem);
    },
    [item, itemUUID, parentUUID, blockStyles],
  );

  const addHighlightToItem = useCallback(
    (item, blockStyles) => {
      let target = document.getElementById(item?.itemUUID);
      if (item?.blockType === TEXT_BLOCK) {
        target = document.getElementById(`q-container-${item?.itemUUID}`);
      }
      if (target) {
        let styles = target?.getBoundingClientRect();
        const editingContainer = document.getElementById(item?.parentUUID);
        const pageContent = editingContainer.getBoundingClientRect();
        let blockAbsoluteStyles = {
          top: parseFloat(styles?.top - pageContent.top).toFixed(3) + 'px',
          left: parseFloat(styles?.left - pageContent.left).toFixed(3) + 'px',
          width: parseFloat(styles?.width - 2).toFixed(3) + 'px',
          height: parseFloat(styles?.height).toFixed(3) + 'px',
          isVisible: 'visible',
        };
        setSelectedBlockStyle(blockAbsoluteStyles);
      }
    },
    [item],
  );

  const removeHighlightOfItem = useCallback(() => {
    setSelectedBlockStyle({ isVisible: 'hidden' });
  }, []);

  const closeSideBarSettings = useCallback(() => {
    removeHighlightOfItem();
    openSettingsInSidebar(false);
  }, []);

  const openSection = useCallback(() => {
    if (item.blockId) {
      if (isEmailTemplate) {
        window.open(`/complexEmailBlock/${item.blockId}`);
      } else {
        window.open(`/block/${item.blockId}`);
      }
    }
  }, [item, isEmailTemplate]);

  const makeReusableSection = useCallback(() => {
    updateArtboard({ [CONTEXT_KEYS.REUSABLE_ASSET_MODAL]: true });
    methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA]: {type:'block'} });
  },[]);

  useEffect(() => {
    !!standaloneBlockEditUUID && standaloneBlockEditUUID !== itemUUID && updateArtboard({ [CONTEXT_KEYS.STANDALONE_BLOCK_EDIT] :''});
    !isBlockClick && updateArtboard({ [CONTEXT_KEYS.STANDALONE_BLOCK_EDIT] :''});
  }, [itemUUID, isBlockClick])

  const selectedBlockRef = useRef(null);
  useEffect(() => {
    selectedBlockRef.current = selectedBlockItem;
  }, [selectedBlockItem]);

  const onPageScroll = useCallback((e) => {
    setPageScrolling(true);    
  }, []);

  const onPageScrollEnd = useCallback((e) => {
    setPageScrolling(false);
    setBlockElement(previous => Object.assign({}, previous));

    const selectedBlockItem = selectedBlockRef.current;
    if(selectedBlockItem) {
      let target = document.getElementById(selectedBlockItem?.itemUUID);
      if (selectedBlockItem?.item?.blockType === TEXT_BLOCK) {
        target = document.getElementById(`q-container-${selectedBlockItem?.itemUUID}`);
      }
      const editingContainer = document.getElementById(selectedBlockItem?.parentUUID);
      const block = target.getBoundingClientRect();
      const pageContent = editingContainer.getBoundingClientRect();

      const style = {
        top: parseFloat(block.top - pageContent.top).toFixed(3) + 'px',
        left: parseFloat(block.left - pageContent.left).toFixed(3) + 'px',
        width: parseFloat(block?.width - 2).toFixed(3) + 'px',
        height: parseFloat(block?.height).toFixed(3) + 'px',
        isVisible: 'visible',
      }

      setSelectedBlockStyle({ ...selectedBlockStyle, ...style });
    }
  }, []);

  useEffect(() => {
    const editingContainer = document.getElementById(`vertical-layout-${pageId}`);
    if (editingContainer) {
      editingContainer.addEventListener('scroll', onPageScroll);
      editingContainer.addEventListener('scrollend', onPageScrollEnd);
      return () => {
        editingContainer.removeEventListener('scroll', onPageScroll);
        editingContainer.removeEventListener('scrollend', onPageScrollEnd);
      }
    }
  }, []);

  const showBlockSettings = useCallback(() => {
    const isEditSectionVisible = item?.isStandalone && isDocumentOrFlowDesign && !isInteractiveDesign;
    const isSettingsVisible = true;
    return (
      <DynamicCellSettings
        absoluteStyles={absoluteStyles}
        elmstyle={{
          top: blockStyles?.top,
          right: blockStyles?.right,
        }}
        className="dynamicCellSettings"
        isVisible={isBlockClick}
        onMouseEnter={(e) => {
          togglePointer(true)
        }}
        onMouseLeave={(e) => {
          togglePointer(false);
          setToggleMovement(false);
        }}
      >
        <div className="border">
          <div className="outline-top" />
          <div className="outline-right" />
          <div className="outline-bottom" />
          <div className="outline-left" />
        </div>
        {isEditSectionVisible || isSettingsVisible ? <div className="box-menu">
          <div className="menu">
            {blockSelected ? (
              <Tooltip
                title={t(
                  BLOCK_SETTINGS_OPTIONS.MAKE_REUSABLE_SECTION_TOOLTIP.key,
                  BLOCK_SETTINGS_OPTIONS.MAKE_REUSABLE_SECTION_TOOLTIP.value,
                )}
              >
                <Icon type="copy" onClick={makeReusableSection} />
              </Tooltip>
            ) : (
              <>
                {isEditSectionVisible ? (
                  <>
                    {standaloneBlockEditUUID === itemUUID ? (
                      <PageActionItem refrencedSection={true}>
                        <Tooltip
                          defaultVisible={standaloneBlockEditUUID === itemUUID}
                          title={t(
                            BLOCK_SETTINGS_OPTIONS.EDIT_SECTION_TOOLTIP.key,
                            BLOCK_SETTINGS_OPTIONS.EDIT_SECTION_TOOLTIP.value,
                          )}
                        >
                          <Icon type="edit" onClick={openSection} />
                        </Tooltip>
                      </PageActionItem>
                    ) : (
                      <Tooltip
                        title={t(
                          BLOCK_SETTINGS_OPTIONS.EDIT_SECTION_TOOLTIP.key,
                          BLOCK_SETTINGS_OPTIONS.EDIT_SECTION_TOOLTIP.value,
                        )}
                      >
                        <Icon type="edit" onClick={openSection} />
                      </Tooltip>
                    )}
                  </>
                ) : null}
                {isSettingsVisible ? (
                  <Tooltip
                    title={t(
                      BLOCK_SETTINGS_OPTIONS.SETTINGS_TOOLTIP.key,
                      BLOCK_SETTINGS_OPTIONS.SETTINGS_TOOLTIP.value,
                    )}
                  >
                    <Icon
                      type="setting"
                      id={`settings-tooltip-${item?.uuid ?? ''}`}
                      onClick={() => {
                        changeSettingOfItem(item);
                        openSettingsInSidebar(true);
                      }}
                    />
                  </Tooltip>
                ) : null}
                  {editable && (isInteractiveDesign ? !!item?.isEditable : true) && !['header', 'footer'].includes(reusableBlockType) && (
                  <Tooltip
                    title={t(
                      BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_TOOLTIP.key,
                      BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_TOOLTIP.value,
                    )}
                  >
                    <Popconfirm
                      title={t(
                        BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_POPUP_LABEL.key,
                        BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_POPUP_LABEL.value,
                      )}
                      okText={t(
                        BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_YES_BUTTON.key,
                        BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_YES_BUTTON.value,
                      )}
                      cancelText={t(
                        BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_NO_BUTTON.key,
                        BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_NO_BUTTON.value,
                      )}
                      placement="left"
                      trigger="click"
                      visible={toggleMovement && isBlockClick}
                      onConfirm={() => {
                        onDeleteBlockClick();
                      }}
                      onCancel={() => togglePointer(false)}
                      overlayStyle={{ width:350 }}
                    >
                      <Icon type="delete" onClick={() => setToggleMovement(!toggleMovement)} />
                    </Popconfirm>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div> : null}
      </DynamicCellSettings>
    );
  }, [picker, item, blockStyles, toggleMovement, isBlockClick, selectedBlocks, blockSelected, standaloneBlockEditUUID]);
  
  return (
    <div style={{ position: 'relative' }}>
      {!blockEditing && blockElement?.blockId && !pageScrolling && showBlockSettings()}
      {(isHighlight || (!pageScrolling && selectedBlockItem ))&& 
      ReactDOM.createPortal(<BlockHighlighter blockStyle={selectedBlockStyle}>
        <div className="border">
          {editable && (<div className="tag">
            {t(
              BLOCK_SETTINGS_OPTIONS.BLOCK_SETTINGS_TAG_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.BLOCK_SETTINGS_TAG_LABEL.value,
            )}
          </div>)}
          <div className="outline-top" />
          <div className="outline-right" />
          <div className="outline-bottom" />
          <div className="outline-left" />
        </div>
      </BlockHighlighter>, document.getElementById(isHighlight?currentFlowId:selectedBlockItem?.parentUUID))}
      {showSideBar && selectedBlockItem !== null &&
        ReactDOM.createPortal(
          <ComplexBlockSettings
            methods={methods}
            selectedItem={selectedBlockItem}
            closeSideBar={closeSideBarSettings}
            editable={editable}
          />,
          document.getElementById('sidebar-absolute-container'),
        )}
      {showSideBar && selectedBlockItem !== null &&
        ReactDOM.createPortal(
          <div
            className="layoutbtn-container"
            onClick={closeSideBarSettings}
            style={{ position: 'absolute', top: 0, left: -2 }}
          >
            <Icon type={'close'} />
          </div>,
          document.getElementById('sidebar-absolute-close'),
        )}
    </div>
  );
};

export default React.memo(BlockLayoutSettings);
